import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import './onboarding/onboarding.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);

  // Pre-fill email from signup redirect if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('email')) setEmail(params.get('email'));
    if (params.get('expired')) setTrialExpired(true);
  }, []);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Incorrect email or password');
        setLoading(false);
        return;
      }

      localStorage.setItem('cia_token', result.token);
      localStorage.setItem('cia_customer', JSON.stringify(result.customer));

      // If trial expired, go to settings/billing
      if (result.trial_expired) {
        navigate('/settings?tab=billing&expired=true');
      } else {
        navigate('/');
      }

    } catch (e) {
      setError('Connection failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="ob-container">
      <header className="ob-header">
        <div className="ob-logo">
          <div className="ob-logo-icon">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="11" stroke="#00C896" strokeWidth="2.4"/>
              <circle cx="14" cy="14" r="4" fill="#00C896"/>
              <path d="M19 18.5L21.2 20.7L25.5 15.5" stroke="#3B82F6" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="ob-logo-text">Aizaan</span>
        </div>
      </header>

      <div className="ob-body">
        <div className="ob-card">
          <h1 className="ob-title">Welcome back</h1>
          <p className="ob-subtitle">Sign in to your Aizaan account</p>

          {trialExpired && (
            <div style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8, padding: '12px 16px', marginBottom: 16,
              fontSize: 13, color: '#ef4444',
            }}>
              Your trial has expired. Sign in to upgrade and reactivate your account.
            </div>
          )}

          <div className="ob-field">
            <label className="ob-label">Email Address</label>
            <input
              className="ob-input"
              type="email"
              placeholder="you@yourbusiness.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              autoFocus
            />
          </div>

          <div className="ob-field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label className="ob-label" style={{ margin: 0 }}>Password</label>
              <button
                onClick={() => setShowForgot(!showForgot)}
                style={{ background: 'none', border: 'none', color: 'var(--blue-light)', fontSize: 12, cursor: 'pointer' }}
              >
                Forgot password?
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                className="ob-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="Your password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                style={{ paddingRight: 40 }}
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none',
                  cursor: 'pointer', color: 'var(--text-muted)'
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {showForgot && (
            <div style={{
              background: 'rgba(59,130,246,0.08)',
              border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: 8, padding: '12px 16px', marginBottom: 16,
              fontSize: 13, color: 'var(--text-secondary)',
            }}>
              To reset your password, email us at{' '}
              <a href="mailto:support@aizaan.app" style={{ color: 'var(--blue-light)' }}>
                support@aizaan.app
              </a>{' '}
              with the subject "Password Reset" and we'll get back to you within a few hours.
            </div>
          )}

          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8, padding: '10px 14px', marginBottom: 16,
              fontSize: 13, color: 'var(--red)',
            }}>
              {error}
            </div>
          )}

          <button
            className="ob-btn ob-btn-primary"
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In →'}
          </button>

          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 16 }}>
            Don't have an account?{' '}
            <a href="/signup" style={{ color: 'var(--blue-light)' }}>Start free trial</a>
          </p>
        </div>
      </div>
    </div>
  );
}
